// ResourceAccessView — provisioning / deprovisioning / entitlement-adjustment
// body. The person card matches the access-approval requester (manager / status
// / start); the resource card carries type, domain, deeplink, catalog + any
// requested duration / comment, none of which an entitlement task shows: its
// access's request predates the task.
// design ref: drawers.jsx ProvisioningResource / DeproResource.

import { Box, Icon, Link, Text } from "@chakra-ui/react";
import { TbClock, TbExternalLink } from "react-icons/tb";

import Chip from "components/ds/Chip";
import SectionHeader from "components/ds/SectionHeader";
import type { MFResourceAccessEntity, MFTaskEntityType } from "state/members/tasks";

import PersonCard from "./PersonCard";
import ResourceDetailCard, { ResourceRow } from "./ResourceDetailCard";
import {
  isEntitlementTask,
  readAccessRequestContext,
  readEntitlementSystem,
  resourceTypeLabel,
} from "./taskMeta";

const Section = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box>
    <SectionHeader>{label}</SectionHeader>
    {children}
  </Box>
);

export interface ResourceAccessViewProps {
  entity: MFResourceAccessEntity;
  entityType: MFTaskEntityType;
}

const ResourceAccessView = ({ entity, entityType }: ResourceAccessViewProps) => {
  const resourceName =
    entity.resourceName || entity.selectableResourceName || "Resource";
  const typeLabel = resourceTypeLabel(entity.resourceType);
  const request = readAccessRequestContext(entityType, entity);
  const system = isEntitlementTask(entityType) ? readEntitlementSystem(entity) : null;
  const personLabel =
    entityType === "deprovisioning" || entityType === "entitlement_deprovisioning"
      ? "Employee"
      : "Member";

  return (
    <>
      {entity.member && (
        <Section label={personLabel}>
          <PersonCard member={entity.member} showHrContext />
        </Section>
      )}

      <Section label="Resource">
        <ResourceDetailCard
          name={resourceName}
          imageUrl={entity.resourceImageUrl}
          catalogItemName={request.catalogItemName}
          catalogName={request.catalogName}
        >
          {typeLabel && (
            <ResourceRow label="Type">
              <Chip tone="azurite">{typeLabel}</Chip>
            </ResourceRow>
          )}
          {entity.resourceDomain && (
            <ResourceRow label="Domain">
              <Text as="span" fontWeight={500} color="neutral.950">
                {entity.resourceDomain}
              </Text>
            </ResourceRow>
          )}
          {entity.resourceDeeplink && (
            <ResourceRow label="Link">
              <Link
                href={entity.resourceDeeplink}
                isExternal
                display="inline-flex"
                alignItems="center"
                gap="2xs"
                color="p.600"
                fontWeight={500}
                _hover={{ color: "p.700", textDecoration: "underline" }}
              >
                Open
                <Icon as={TbExternalLink} boxSize="12px" />
              </Link>
            </ResourceRow>
          )}
          {system?.integrationName && (
            <ResourceRow label="In">
              <Text as="span" fontWeight={500} color="neutral.950">
                {system.integrationName}
                {system.connectionName && (
                  <Text as="span" fontWeight={400} color="neutral.700">
                    {" · "}
                    {system.connectionName}
                  </Text>
                )}
              </Text>
            </ResourceRow>
          )}
          {system?.accountIdentifier && (
            <ResourceRow
              label={system.integrationName ? `${system.integrationName} user` : "Identity"}
            >
              <Text as="span" fontWeight={500} color="neutral.950" wordBreak="break-all">
                {system.accountIdentifier}
              </Text>
            </ResourceRow>
          )}
          {request.duration && (
            <ResourceRow label="Requested for">
              <Chip icon={<Icon as={TbClock} boxSize="12px" />}>{request.duration}</Chip>
            </ResourceRow>
          )}
          {request.comment && (
            <ResourceRow label="Comment">
              <Text as="span" fontWeight={400} color="neutral.800">
                {request.comment}
              </Text>
            </ResourceRow>
          )}
        </ResourceDetailCard>
      </Section>
    </>
  );
};

export default ResourceAccessView;
